<template>
  <div class="container">
    <!-- 日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。 -->
    <div>
      <md-radio
        v-for="term in filteredTermTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
      <!-- v-if="visibleTermTypes.includes(term.id)" -->
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <!-- 週の選択 -->
        <div class="term-input" v-if="isWeekMode" key="w">
          <gl-input-date :min="minDate" :max="maxDate" :value.sync="_week" />
        </div>
        <!-- 日の選択 -->
        <div class="term-input" v-if="isDateMode" key="d">
          <gl-input-date :min="minDate" :max="maxDate" :value.sync="_date" />
        </div>
        <!-- 月の選択 -->
        <div class="term-input" v-if="isMonthMode" key="m">
          <gl-select-month :min="minDate" :max="maxDate" :value.sync="month" />
        </div>
      </transition>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>
<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import { dateLimit, toBasicDateFormat } from '@/util/date'

import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'
/**
 * 期間選択コンポーネント
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  mixins: [QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [
        { id: 'w', label: '週' },
        { id: 'm', label: '月' },
        { id: 'd', label: '日' },
      ],
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  components: { GlSelectMonth, GlInputDate },
  props: {
    visibleTermTypes: {
      type: Array,
      default: () => ['w', 'm', 'd'],
    },

    // 選択している選択種別（日 / 週 / 月）
    termType: {
      type: String,
      default: 'w',
    },
    date: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     */
    maxDate() {
      this.adjustRangeIfNeeded()
    },
    minDate() {
      this.adjustRangeIfNeeded()
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     */
    termType(value) {
      this.$emit('update:termType', value)
      let date = this.date
      // dateはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateをemitするだけです。
      switch (value) {
        case 'd':
          break
        case 'w':
          break
        case 'm':
          date = dateLimit(date, this.minDate, this.maxDate)
          this.$emit('update:date', date)
          break
        default:
          break
      }
    },
  },
  methods: {
    adjustRangeIfNeeded() {
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const dateAdjusted = dateLimit(this.date, this.minDate, this.maxDate)
      if (dateAdjusted !== this.date) {
        this.$emit('update:date', dateAdjusted)
        this.snackbar.message =
          '指定期間が選択可能な期間外だったため、自動調整しました。'
        this.snackbar.show = true
      }
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },
    /**
     *ラジオボタンで押された期間のtermTypeを変更します
     */
    _termType: {
      get() {
        if (!this.visibleTermTypes.includes(this.termType)) {
          this.$emit('update:termType', this.filteredTermTypes[0].id || 'd')
        }
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    isDateMode() {
      return this.termType === 'd'
    },
    isWeekMode() {
      return this.termType === 'w'
    },
    isMonthMode() {
      return this.termType === 'm'
    },
    maxWeek() {
      return moment(this.maxDate).diff(this.date, 'weeks') + 1
    },
    /**
     * termTypeが週で開始日が変更された場合ここで変更保存をしています。
     * x週間の間隔を保つため終了日も一緒に変更保存。
     */
    _week: {
      get() {
        return this.date
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:date', newVal)
      },
    },
    // このコンポーネントの入出力となる開始・終了日のフォーマットと、
    // inputに設定する値の日付フォーマットが異なるので変換用computedプロパティを用意
    /**
     * termTypeが日
     */
    _date: {
      get() {
        return this.date
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:date', newVal)
      },
    },
    /**
     * termTypeが月
     */
    month: {
      get() {
        return moment(this.date).format('YYYYMM')
      },
      set(newVal) {
        newVal = toBasicDateFormat(newVal)
        newVal = moment(newVal)
          .startOf('month')
          .format('YYYYMMDD')
        newVal = dateLimit(newVal, this.minDate, this.maxDate)

        this.$emit('update:date', newVal)
      },
    },
    /**
     * 期間のラジオボタン
     */
    filteredTermTypes() {
      return this.termTypes.filter(f => this.visibleTermTypes.includes(f.id))
    },
  },

  /**
   * APIから選択出来る期限のカレンダーを取得します
   */
  async mounted() {
    // 検索期間の初期値指定、任意の初期値がある場合はelseへ
    // 検索期限は外部APIから取得する必要がある為、ここに記述
    let date = this.date
    if (!date) {
      date = toBasicDateFormat(this.maxDate)
      // 検索期間が検索期限を超えていた場合の処理
      date = dateLimit(date, this.minDate, this.maxDate)
      this.$emit('update:date', date)
    }
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.term-input {
  display: flex;
}

.week-count {
  width: 70px;
}

.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
}

// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}
</style>
